import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fulfillmentApi } from '../../api/fulfillment-api';
import { ConflictError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import type { FulfillmentTemplate } from '../../domain/fulfillment';
import { fulfillmentErrorMessage, isUnknownOutcome } from './error-message';
import { RequirementsEditor } from './RequirementsEditor';
import { newRequirementRow, requirementRowsError, type RequirementRow } from './requirement-rows';
import { useFulfillmentReferences } from './use-fulfillment-references';

/**
 * Creates or edits a template: a named installation configuration such as
 * "Gen2 winch kit". Orders copy its lines when created, so edits here
 * never change existing orders.
 */
export function TemplateEditorDrawer({
  template,
  onClose,
}: {
  template?: FulfillmentTemplate | undefined;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const references = useFulfillmentReferences();
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [active, setActive] = useState(template?.active ?? true);
  const [rows, setRows] = useState<RequirementRow[]>(() =>
    (template?.requirements ?? []).map((requirement) =>
      newRequirementRow(requirement.type, requirement),
    ),
  );
  const [error, setError] = useState<{ text: string; conflict: boolean } | null>(null);
  const [unknown, setUnknown] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const input = {
        name: name.trim(),
        // On update '' clears it; an omitted description leaves it unchanged.
        description: template ? description.trim() : description.trim() || undefined,
        active,
        requirements: rows.map((row) => row.requirement),
      };
      return template
        ? fulfillmentApi.updateTemplate(template.id, template.version, input)
        : fulfillmentApi.createTemplate(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['fulfillment-templates'] });
      onClose();
    },
    onError: (failure: unknown) => {
      if (isUnknownOutcome(failure)) setUnknown(true);
      else {
        setError({
          text:
            failure instanceof ConflictError
              ? 'Someone else changed this template. Close and reopen it to see their changes.'
              : fulfillmentErrorMessage(failure, 'The template could not be saved.'),
          conflict: failure instanceof ConflictError,
        });
      }
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError({ text: 'Give the template a name.', conflict: false });
      return;
    }
    if (rows.length === 0) {
      setError({ text: 'A template needs at least one line.', conflict: false });
      return;
    }
    const rowError = requirementRowsError(rows);
    if (rowError) {
      setError({ text: rowError, conflict: false });
      return;
    }
    save.mutate();
  }

  const title = template ? `Edit ${template.name}` : 'New template';
  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close template editor"
          >
            ×
          </button>
        </div>
        {error ? (
          <p role="alert" className="form-error">
            {error.text}
          </p>
        ) : null}
        {unknown ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['fulfillment-templates'] });
              onClose();
            }}
          />
        ) : null}
        <label>
          Name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Gen2 winch kit"
            required
          />
        </label>
        <label>
          Description
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
          />
        </label>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          <span>Available for new orders</span>
        </label>
        <RequirementsEditor label="Lines" rows={rows} onChange={setRows} references={references} />
        {template ? (
          <p className="field-hint">Existing orders keep the lines they were created with.</p>
        ) : null}
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : template ? 'Save template' : 'Create template'}
          </button>
        </div>
      </form>
    </div>
  );
}
